<template>
  <GTag v-if="!isDev" />
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import {
  type Meta,
  type Link,
  useHead,
  useServerHead,
  useSeoMeta,
} from '@unhead/vue'
import { useSchemaOrg, defineOrganization } from '@unhead/schema-org/vue'
import { useRoute } from 'vue-router'

import { isClient } from '/@shared/app'

import { useOpenGraphCard } from '.'

import GTag from '/@/modules/app/GTag.vue'

import { useI18n, type AppLocale } from '/@/i18n'
import { useAppStore, usePagesStore } from '/@/stores'

import favicon from '/@/assets/images/favicon.ico'
import faviconDev from '/@/assets/images/favicon-dev.ico'
import svgIcon from '/@/assets/images/icon.svg'
import svgIconDev from '/@/assets/images/icon-dev.svg'
import appleTouch from '/@/assets/images/apple-touch-icon.png'
import appleTouchDev from '/@/assets/images/apple-touch-icon-dev.png'
import fornexLogo from '/@/assets/images/logo.svg?no-inline'

const appStore = useAppStore()
const pagesStore = usePagesStore()

const { t } = useI18n()

const route = useRoute()

const isDev = import.meta.env.DEV

const pageContext = computed(() => pagesStore.currentPageContext)

useSeoMeta({
  // @ts-expect-error invalid type: we could safely use undefined here
  title: computed(() => {
    let _title = pageContext.value?.seo_title || ''

    if (pageContext.value?.seo_title_optional)
      _title += ' – ' + pageContext.value.seo_title_optional

    if (_title) return `${_title} | Fornex`

    if (route.name === 'HomePage') return t('seo./.title')
    if (route.name === 'ServicesPage') return t('seo.services.title')
    if (route.name === 'BackupPage') return t('seo.backups.title')
    if (route.name === 'VpsPage') return t('seo.vps.title')
    if (route.name === 'VhPage') return t('seo.hosting.title')
    if (route.name === 'DsPage') return t('seo.dedicated-servers.title')

    return undefined
  }),
  description: computed(() => {
    if (pageContext.value?.seo_description)
      return pageContext.value.seo_description

    if (route.name === 'HomePage') return t('seo./.description')
    if (route.name === 'ServicesPage') return t('seo.services.description')
    if (route.name === 'BackupPage') return t('seo.backups.description')
    if (route.name === 'VpsPage') return t('seo.vps.description')
    if (route.name === 'VhPage') return t('seo.hosting.description')
    if (route.name === 'DsPage') return t('seo.dedicated-servers.description')

    return undefined
  }),
  keywords: computed(() => {
    if (pageContext.value?.seo_keywords) return pageContext.value.seo_keywords

    if (route.name === 'HomePage') return t('seo./.keywords')
    if (route.name === 'ServicesPage') return t('seo.services.keywords')
    if (route.name === 'BackupPage') return t('seo.backups.keywords')
    if (route.name === 'VpsPage') return t('seo.vps.keywords')
    if (route.name === 'VhPage') return t('seo.hosting.keywords')
    if (route.name === 'DsPage') return t('seo.dedicated-servers.keywords')

    return undefined
  }),
})

useOpenGraphCard()

useSchemaOrg(
  defineOrganization({
    name: 'Fornex',
    image: fornexLogo,
    url: 'https://fornex.com/',
    telephone: '+1 707 706 0739',
    legalName: computed(() => appStore.publicCompanies?.[0].name),
    address: computed(() =>
      appStore.publicCompanies?.length
        ? {
            streetAddress: appStore.publicCompanies[0].address.join(','),
            postalCode: '',
            addressCountry: '',
          }
        : undefined
    ),
  })
)

const metaList = computed(() => {
  const list: Meta[] = [
    {
      property: 'google-site-verification',
      content: 'ey17UUmJgpqsTTVlOTKxvymjVzQW3h774UzzqqzS20M',
    },
    {
      property: 'google-site-verification',
      content: 'rxK_BjQStXCqkqyJu77TTTQX3lTSQdxysqUomyDWvko',
    },
    {
      property: 'yandex-verification',
      content: '63ea1a6cadbc918d',
    },
    {
      property: 'alexaVerifyID',
      content: '4nL0hHfRHmYXNkOwXfFRQ45OZ',
    },
  ]

  return list
})

const isProduction = import.meta.env.MODE === 'production'

if (isClient) {
  document.documentElement.classList.remove('u-no-transition')
}

useServerHead({
  htmlAttrs: {
    class: 'u-no-transition',
  },
})

useHead({
  htmlAttrs: {
    lang: computed(
      () => ((route.params as { lang: string }).lang as AppLocale) || 'en'
    ),
    prefix: () =>
      route.name === 'ContactsPage'
        ? 'og: http://ogp.me/ns# fb: http://ogp.me/ns/fb# business: http://ogp.me/ns/business#'
        : undefined,
  },

  link: computed(() => {
    const list: Link[] = [
      {
        rel: 'icon',
        href: isProduction ? favicon : faviconDev,
        sizes: '32x32',
      },
      {
        rel: 'icon',
        href: isProduction ? svgIcon : svgIconDev,
        type: 'image/svg+xml',
      },
      {
        rel: 'apple-touch-icon',
        href: isProduction ? appleTouch : appleTouchDev,
      },

      { rel: 'canonical', href: 'https://fornex.com' + route.path },
    ]

    if (route && 'lang' in route.params) {
      let actualPath = route.params.lang
        ? route.path.split('/').slice(2).join('/')
        : route.path

      if (actualPath[0] === '/') actualPath = actualPath.slice(1)

      list.push(
        {
          rel: 'alternate',
          href: 'https://fornex.com/' + actualPath,
          hreflang: 'en-us',
        },
        {
          rel: 'alternate',
          href: 'https://fornex.com/ru/' + actualPath,
          hreflang: 'ru-ru',
        }
      )
    }

    return list
  }),

  meta: metaList,
})
</script>
